import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import NotificationPrompt from '../components/NotificationPrompt';
import { listenForForegroundMessages, showToast } from '../notifications';

function JobCard({ job }) {
  const [expanded, setExpanded] = useState(false);

  const statusLabels = {
    pending: 'Awaiting review',
    quoted: 'Quote ready',
    active: 'In progress',
    complete: 'Completed',
    invoiced: 'Invoice sent',
    paid: 'Paid ✓',
    declined: 'Declined',
  };

  const statusDescriptions = {
    pending: 'Your request has been received. Dean will review it and be in touch within 24 hours.',
    quoted: 'Dean has sent you a quote. Review it below and let him know if you\'d like to go ahead.',
    active: 'Dean is currently working on your job.',
    complete: 'The work is complete. An invoice will follow shortly.',
    invoiced: 'An invoice has been sent — go to the Invoices tab to view and pay.',
    paid: 'Payment received. Thank you for using Garden & Groundworks!',
    declined: 'This quote was declined.',
  };

  async function handleAccept() {
    await updateDoc(doc(db, 'quotes', job.id), { status: 'active' });
    setExpanded(false);
  }

  async function handleDecline() {
    await updateDoc(doc(db, 'quotes', job.id), { status: 'declined' });
    setExpanded(false);
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ flex: 1, marginRight: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 3 }}>{job.address || 'Site address TBC'}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {job.services?.join(', ') || 'General horticulture'}
          </div>
        </div>
        <span className={`badge badge-${job.status}`}>{statusLabels[job.status] || job.status}</span>
      </div>

      {/* Plain-English status description */}
      {statusDescriptions[job.status] && (
        <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', background: 'var(--bg)',
          borderRadius: 8, padding: '8px 10px', marginBottom: 8, lineHeight: 1.5 }}>
          {statusDescriptions[job.status]}
        </div>
      )}

      {/* Agreed price */}
      {job.quoteAmount && (
        <div style={{ background: 'var(--green-pale)', borderRadius: 8, padding: '8px 12px', marginBottom: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600, color: 'var(--green-dark)' }}>
            {job.status === 'quoted' ? 'Quote' : 'Agreed price'}: £{job.quoteAmount.toFixed(2)}
          </span>
          {job.status === 'quoted' && (
            <button onClick={() => setExpanded(!expanded)}
              style={{ fontWeight: 600, color: 'var(--green-mid)', background: 'none', border: 'none',
                cursor: 'pointer', fontSize: '0.9rem', padding: 0 }}>
              {expanded ? 'Close ↑' : 'Review →'}
            </button>
          )}
        </div>
      )}

      {/* Scheduled next visit */}
      {job.nextDate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#E3F2FD',
          borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
          <span style={{ fontSize: '0.85rem', color: '#1565C0' }}>
            📅 Next visit: <strong>{new Date(job.nextDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
          </span>
        </div>
      )}

      {/* Quote review / accept / decline */}
      {expanded && job.status === 'quoted' && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          {job.description && (
            <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
              <strong>Job description:</strong> {job.description}
            </div>
          )}
          {job.preferredDate && (
            <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 10 }}>
              <strong>Preferred start:</strong> {job.preferredDate}
            </div>
          )}
          <div style={{ background: 'var(--green-pale)', borderRadius: 8, padding: '14px', marginBottom: 12, textAlign: 'center' }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 4 }}>Quote amount</div>
            <div style={{ fontSize: '1.7rem', fontWeight: 800, color: 'var(--green-dark)' }}>£{job.quoteAmount.toFixed(2)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleAccept} className="btn-primary" style={{ flex: 1, padding: '12px', fontSize: '0.95rem' }}>
              ✓ Accept quote
            </button>
            <button onClick={handleDecline}
              style={{ flex: 1, padding: '12px', fontSize: '0.95rem', background: 'white',
                border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              ✗ Decline
            </button>
          </div>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
            Questions? Send Dean a message below.
          </p>
        </div>
      )}

      {job.status === 'invoiced' && (
        <div style={{ padding: '8px 12px', background: 'var(--gold-pale)', borderRadius: 8,
          fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
          📄 Invoice sent — go to the Invoices tab to pay
        </div>
      )}
    </div>
  );
}

function NavBar({ active }) {
  return (
    <nav className="bottom-nav">
      <Link to="/dashboard" className={active === 'home' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></svg>
        Jobs
      </Link>
      <Link to="/messages" className={active === 'messages' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        Messages
      </Link>
      <Link to="/quote" className={active === 'quote' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
        New quote
      </Link>
      <Link to="/invoices" className={active === 'invoices' ? 'active' : ''}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        Invoices
      </Link>
    </nav>
  );
}

export function ClientDashboard() {
  const { currentUser, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'quotes'),
      where('email', '==', currentUser.email.toLowerCase()),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const unsubMsg = listenForForegroundMessages((payload) => {
      const { title, body } = payload.notification || {};
      if (title) showToast(title, body, payload.data?.actionUrl);
    });
    return () => { unsub(); if (typeof unsubMsg === 'function') unsubMsg(); };
  }, [currentUser]);

  return (
    <>
      <div className="app-header">
        <div>
          <h1>Garden & Groundworks</h1>
          <div className="subtitle">My jobs</div>
        </div>
        <button onClick={logout} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '6px 12px', fontSize: '0.8rem' }}>Sign out</button>
      </div>
      <div className="page-wrap" style={{ paddingTop: 24 }}>
        {loading ? <div className="spinner"/> : jobs.length === 0 ? (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
            <p>No jobs yet.</p>
            <Link to="/quote"><button className="btn-primary" style={{ width: 'auto', marginTop: 16, padding: '12px 24px' }}>Request your first quote</button></Link>
          </div>
        ) : jobs.map(job => <JobCard key={job.id} job={job} />)}
      </div>
      <NotificationPrompt />
      <NavBar active="home" />
    </>
  );
}

export function MessagesPage() {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'messages'),
      where('clientEmail', '==', currentUser.email.toLowerCase()),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [currentUser]);

  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    const { addDoc, serverTimestamp } = await import('firebase/firestore');
    await addDoc(collection(db, 'messages'), {
      clientEmail: currentUser.email.toLowerCase(),
      from: 'client',
      text: text.trim(),
      createdAt: serverTimestamp(),
    });
    setText('');
  }

  return (
    <>
      <div className="app-header"><div><h1>Garden & Groundworks</h1><div className="subtitle">Messages</div></div></div>
      <div className="page-wrap" style={{ paddingTop: 24 }}>
        <div style={{ marginBottom: 80 }}>
          {messages.length === 0 && <div className="empty-state"><p>No messages yet. Send a message to Dean below.</p></div>}
          {messages.map(m => (
            <div key={m.id} style={{ display: 'flex', justifyContent: m.from === 'client' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
              <div style={{ maxWidth: '80%', background: m.from === 'client' ? 'var(--green-mid)' : 'white', color: m.from === 'client' ? 'white' : 'var(--text-primary)', borderRadius: m.from === 'client' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', padding: '10px 14px', boxShadow: 'var(--shadow)', fontSize: '0.95rem' }}>
                {m.text}
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={send} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', gap: 8, paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}>
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Type a message..." style={{ flex: 1 }} />
          <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 20px' }}>Send</button>
        </form>
      </div>
      <NavBar active="messages" />
    </>
  );
}

function PayButton({ inv }) {
  const navigate = useNavigate();
  return (
    <button className="btn-gold" style={{ marginTop: 12, padding: '10px' }}
      onClick={() => navigate(`/pay/${inv.id}`, { state: { invoice: inv } })}>
      Pay now
    </button>
  );
}

export function InvoicesPage() {
  const { currentUser } = useAuth();
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'invoices'), where('clientEmail', '==', currentUser.email.toLowerCase()));
    const unsub = onSnapshot(q, snap => setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [currentUser]);

  return (
    <>
      <div className="app-header"><div><h1>Garden & Groundworks</h1><div className="subtitle">Invoices</div></div></div>
      <div className="page-wrap" style={{ paddingTop: 24 }}>
        {invoices.length === 0 ? <div className="empty-state"><p>No invoices yet.</p></div> : invoices.map(inv => (
          <div key={inv.id} className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{inv.description}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 2 }}>{inv.createdAt?.toDate?.()?.toLocaleDateString?.('en-GB')}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>£{inv.amount?.toFixed(2)}</div>
                <span className={`badge badge-${inv.paid ? 'paid' : 'invoiced'}`}>{inv.paid ? 'Paid' : 'Outstanding'}</span>
              </div>
            </div>
            {!inv.paid && <PayButton inv={inv} />}
          </div>
        ))}
      </div>
      <NavBar active="invoices" />
    </>
  );
}
